/**
 * Wedding Books — co-located API client.
 *
 * Talks to admin-backend `/api/admin/wedding/*` (backend/routes/admin/weddingAdmin.js).
 * Per-section client rather than another 3,000 lines in services/api.ts — the
 * same convention the Quill pages follow (components/quill/flags/api.ts).
 *
 * AUTH is the admin_access_token cookie, so every request sets
 * `credentials: 'include'` exactly like services/api.ts does. There is no
 * Authorization header and no token in JS.
 *
 * ENVELOPE. The router replies `{ success: true, data }` on success and
 * `{ success: false, message, error }` on failure — including on some 4xx where
 * the HTTP status alone would be ambiguous. Both are handled: anything that is
 * not a 2xx with `success !== false` throws WeddingApiError carrying the HTTP
 * status and the machine-readable `error` code (ALREADY_SUSPENDED,
 * DEADLINE_IN_PAST, REMINDER_CEILING_REACHED, …) so a caller can branch on the
 * code instead of matching prose.
 *
 * NUMERIC TYPES. Postgres bigint (SUM(amount_cents)::bigint) and numeric come
 * back from node-pg as STRINGS, so every money/count field is typed
 * `number | string` and must be read through `num()` in weddingFormat.ts.
 */

const BASE = '/api/admin/wedding';

/** Thrown for any non-2xx response or any `{ success: false }` body. */
export class WeddingApiError extends Error {
  status: number;

  code?: string;

  /**
   * The `data` object from a REFUSAL body, when the server sent one.
   *
   * Some 4xx responses are not failures to report but steps in a flow, and
   * they carry the numbers the next step needs — OVER_CONTRIBUTOR_CAP returns
   * `{ live_guests, new_cap }` so the dialog can say "this book has 180
   * contributors; Premium allows 150" using the SERVER's count rather than
   * recomputing it from a row that may be stale. Without this the dialog would
   * silently fall back to its own arithmetic and quietly disagree.
   */
  details?: Record<string, unknown>;

  constructor(message: string, status: number, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'WeddingApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface Envelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/** Builds `?a=1&b=2`, skipping undefined/null/empty values. */
function qs(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
    });
  } catch (e) {
    throw new WeddingApiError(
      e instanceof Error ? e.message : 'Network error contacting the admin API',
      0,
      'NETWORK_ERROR'
    );
  }

  let body: Envelope<T> | null = null;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch {
    // No/invalid JSON body — handled below.
  }

  if (!res.ok || (body && body.success === false)) {
    throw new WeddingApiError(
      (body && (body.message || body.error)) || `Request failed (${res.status})`,
      res.status,
      body?.error,
      body && body.data && typeof body.data === 'object'
        ? (body.data as unknown as Record<string, unknown>)
        : undefined
    );
  }

  return (body && body.data !== undefined ? body.data : ({} as T));
}

// ── Types ───────────────────────────────────────────────────────────────────

/** node-pg hands bigint/numeric back as a string. */
export type Numeric = number | string;

export type BookStatus =
  | 'draft' | 'pending_payment' | 'active' | 'collecting'
  | 'closed' | 'generating' | 'delivered' | 'suspended';

export type Tier = 'classic' | 'premium' | 'luxury' | 'platinum';

export type GuestStatus = 'pending' | 'invited' | 'verified' | 'writing' | 'submitted';

export interface BooksByStatus {
  total: number;
  draft: number;
  pending_payment: number;
  active: number;
  collecting: number;
  closed: number;
  generating: number;
  delivered: number;
  suspended: number;
  created_30d: number;
}

export interface RevenueTotals {
  gross_cents: Numeric;
  gross_cents_30d: Numeric;
  gross_cents_prev_30d: Numeric;
  paid_count: number;
  refunded_count: number;
  failed_count: number;
  pending_count: number;
}

export interface TierMixRow {
  tier: string;
  purchase_type: string;
  cents: Numeric;
  purchases: number;
}

export interface ParticipationTotals {
  total_guests: number;
  invited: number;
  submitted: number;
}

/**
 * Counted in PEOPLE, not rows — see CONVERSION_FUNNEL_PROJECTION in
 * weddingAdmin.js for why the previous shape over-reported twice over.
 *
 * `converted` now EXCLUDES `matched_existing` rows: those are people who
 * already had a LifeScribe account, and counting them credited the wedding
 * funnel with members it did not produce.
 *
 * `touched` and `clicked` are legacy aliases of `people_touched` /
 * `people_clicked`, kept for one release so a stale bundle renders a number
 * rather than `undefined`. Prefer the `people_*` fields in new code.
 */
export interface ConversionFunnel {
  people_touched: number;
  people_clicked: number;
  offered: number;
  converted: number;
  matched_existing: number;
  /** Genuine conversions with no user id — the size of the attribution blind spot. */
  converted_unlinked: number;
  touched: number;
  clicked: number;
}

export interface EngagementTotals {
  sent: number;
  opened: number;
  clicked: number;
  bounced: number;

  // Whether anything on the server actually WRITES per-guest opens/bounces on
  // this database — the server reads its own reconciliation sync state, so
  // these flip to true on their own once that job runs. OPTIONAL, per the
  // stale-bundle rule: a bundle deployed against a backend that predates them
  // must render rather than crash.
  opened_tracked?: boolean;
  bounced_tracked?: boolean;
  tracking_reason?: string | null;
}

export interface ActivityEvent {
  id: string;
  book_id: string;
  guest_id?: string | null;
  event_type: string;
  actor_name: string | null;
  payload: Record<string, unknown>;
  created_at: string;
  wedding_code?: string;
  partner1_name?: string;
  partner2_name?: string;
}

/**
 * GET /activity — the same rows /overview embeds, at a caller-chosen depth.
 *
 * `note` is the honesty caption and belongs to the SERVER: this feed is the
 * latest events across every book, live, and it is NOT scoped to the date
 * filter the rest of the page obeys. A card that polls this must say so in the
 * server's own words rather than in copy that can drift away from the query.
 */
export interface ActivityResponse {
  events: ActivityEvent[];
  limit: number;
  scope: string;
  note: string;
  generated_at: string;
}

// ── Range-scoped shapes (shared by /overview, /members, /revenue) ───────────
//
// Every windowed endpoint answers with the SAME range description, computed
// once server-side (`rangeMeta()` in weddingAdmin.js) so three screens cannot
// disagree about what "the previous period" means. `timezone_note` is part of
// the payload rather than page copy: day boundaries resolve in the admin
// backend's zone (UTC in production), NOT the operator's, and a page must not
// quietly imply otherwise.

export interface RangeMeta {
  token: string;
  custom: boolean;
  from: string;
  to: string;
  prev_from: string;
  prev_to: string;
  days: number;
  timezone_note: string;

  /**
   * True when the window's end was in the future and the server clamped it to
   * now — the period is still running. Its totals are not yet comparable in
   * size to a completed one, so the prior window is aligned to the same clock
   * positions rather than butted up against this one. Optional per the
   * stale-bundle rule.
   */
  partial?: boolean;
  partial_note?: string | null;
}

export interface KpiSeriesPoint {
  date: string;
  value: Numeric;
}

/**
 * One headline number and its prior period.
 *
 * `delta_pct` is NULL when the previous period was zero — "up 100% from
 * nothing" is not a measurement, and the server refuses to invent one. Render
 * that case as "new", never as a percentage.
 */
export interface KpiBlock {
  value: Numeric | null;
  prev: Numeric | null;
  delta_pct: number | null;
  series?: KpiSeriesPoint[];
}

export interface OverviewKpis {
  books_created: KpiBlock;
  revenue_cents: KpiBlock;
  guests_invited: KpiBlock;
  chapters_submitted: KpiBlock;
}

export interface RevenueInRange {
  gross_cents_in_range: Numeric;
  gross_cents_prev_range: Numeric;
  paid_in_range: number;
  refunded_in_range: number;
}

/**
 * A newly created book with its couple.
 *
 * `acquisition_source` is NULL until migration 1111 lands — the column is
 * selected as an explicit NULL so the row shape never changes. There is NO city
 * field anywhere in the wedding schema; the prototype's "Austin, TX" is
 * illustrative and must not be invented here.
 */
export interface NewestCoupleRow {
  book_id: string;
  couple_id: string;
  wedding_code: string;
  partner1_name: string;
  partner2_name: string;
  tier: Tier;
  status: BookStatus;
  created_at: string;
  couple_email: string;
  acquisition_source: string | null;
  acquisition_detail: string | null;
  guests_invited: Numeric;
  guests_total: Numeric;
  paid_cents: Numeric;
}

/**
 * One rung of the invitation funnel.
 *
 * `measurement` is load-bearing. Every step is counted per guest EXCEPT
 * `opened`, which is a Brevo tag-level aggregate: `wedding_invitations.opened_at`
 * has never had a writer, so a per-guest open count would be a structural zero
 * dressed as a measurement. A step measured differently cannot be differenced
 * against its neighbours, which is why the server's biggest-drop skips it.
 */
export interface InvitationFunnelStep {
  key: string;
  label: string;
  value: Numeric | null;
  measurement: 'per_guest' | 'provider_aggregate';
  note?: string;
}

export interface InvitationFunnel {
  steps: InvitationFunnelStep[];
  biggest_drop: { from: string; to: string; lost: number; pct: number } | null;
  opens_available: boolean;
  opens_are_provider_level: true;
  opens_tags: string[];
  opens_delivered: Numeric | null;
  opens_unique_clicks: Numeric | null;
  started_definition: string;
}

/** One cover style or PDF edition, with its share of the range and a trend. */
export interface TemplateUsageRow {
  key: string;
  books_total: Numeric;
  books_in_range: Numeric;
  books_prev_range: Numeric;
  /** NULL when nothing in the range could take a share OF it. */
  share_in_range: number | null;
  share_total: number | null;
  delta: number;
  trend: 'up' | 'down' | 'flat';
  /** NULL when the catalogue table is not in this database — not "false". */
  is_new: boolean | null;
}

export interface TemplateUsage {
  style_key: TemplateUsageRow[];
  pdf_template: TemplateUsageRow[];
  catalogue_available: boolean;
  new_keys: { style_key: string[]; pdf_template: string[] } | null;
}

export type EmailHealth = 'good' | 'watch' | 'poor';

export interface EmailTemplateTagRow {
  tag: string;
  template_key: string;
  requests: Numeric | null;
  delivered: Numeric | null;
  unique_opens: Numeric | null;
  unique_clicks: Numeric | null;
  hard_bounces: Numeric | null;
  soft_bounces: Numeric | null;
  spam_reports: Numeric | null;
  open_rate: number | null;
  click_rate: number | null;
  bounce_rate: number | null;
  /** NULL when nothing was delivered in the window — unknown, not "poor". */
  health: EmailHealth | null;
}

export interface EmailTemplatesSummary {
  available: boolean;
  reason?: string;
  tags: EmailTemplateTagRow[];
  totals: Omit<EmailTemplateTagRow, 'tag' | 'template_key' | 'health' | 'spam_reports'> | null;
  thresholds: { open_rate_good: number; open_rate_watch: number; bounce_rate_warn: number };
  measurement?: string;
  note?: string;
  registry_templates_added_in_range: number | null;
  registry_available: boolean;
}

// ── Q1 · writing quality ────────────────────────────────────────────────────

/** A count measured twice: inside the date filter, and over all of history. */
export interface RangeAndAllTime {
  in_range: number;
  all_time: number;
}

/** The same pair when the number is a rate or a median — NULL at a zero denominator. */
export interface RangeAndAllTimeNullable {
  in_range: number | null;
  all_time: number | null;
}

/**
 * AI-assist adoption, acceptance, median length and thin chapters.
 *
 * TWO THINGS ARE LOAD-BEARING FOR ANY UI THAT READS THIS.
 *
 * 1. `adoption_rate` and `acceptance_rate` are NULL at a zero denominator and
 *    must render '—'. A 0% would say contributors declined a feature that in
 *    fact nothing measured.
 * 2. `eligible` is the ELIGIBILITY denominator — chapters in books where the
 *    AI-polish button actually existed. `enhanced_any_book_all_time` /
 *    `accepted_any_book_all_time` are the SAME counts with no eligibility
 *    predicate at all; a gap between the pair is the population whose couple
 *    turned AI polish off after their contributors had used it. Neither of the
 *    "any book" numbers is the numerator of any rate.
 */
export interface WritingQuality {
  scope: string;
  finished: RangeAndAllTime;
  eligible: RangeAndAllTime;
  enhanced: RangeAndAllTime;
  accepted: RangeAndAllTime;
  thin: RangeAndAllTime;
  enhanced_any_book_all_time: number;
  accepted_any_book_all_time: number;
  median_words: RangeAndAllTimeNullable;
  hidden_all_time: number;
  draft_all_time: number;
  adoption_rate: RangeAndAllTimeNullable;
  acceptance_rate: RangeAndAllTimeNullable;
  thin_threshold_words: number;
  /** Server-shipped prose, one string per measured term. Rendered as tooltips. */
  definitions: Record<string, unknown>;
}

// ── Q3 · the Resemble rapid-clone quota ─────────────────────────────────────

/**
 * Voice-clone slots, point in time.
 *
 * `limit` IS THE SERVER'S NUMBER AND THE UI MUST USE IT. Hardcoding 500 in a
 * component would mean two places to change when the vendor plan changes, and
 * the one an operator reads would be the one nobody remembered.
 *
 * `remaining_at_most` is an upper bound, never a balance — member-side clones
 * draw on the same account-wide pool and this endpoint cannot see them. That is
 * what `caveat` says, and it is shipped precisely so a card cannot omit it.
 */
export interface VoiceQuota {
  scope: string;
  limit: number;
  slots_used: number;
  remaining_at_most: number;
  /** 0–1, or NULL if the limit were ever zero. */
  used_share: number | null;
  ready: number;
  pending: number;
  consented: number;
  failed: number;
  released: number;
  books_holding_slots: number;
  newest_profile_at: string | null;
  caveat: string;
  definition: Record<string, unknown>;
}

// ── E3 · the anniversary re-release programme ───────────────────────────────

export interface AnniversaryOptIn {
  eligible_books: number;
  opted_in: number;
  /** NULL when the opt-out column is not on this database — not "nobody". */
  opted_out: number | null;
  opted_in_any_tier: number;
  /** NULL when no book is on an anniversary tier: no denominator, no rate. */
  opt_in_rate: number | null;
}

/** One edition year's funnel. `declined` is TERMINAL and honoured forever. */
export interface AnniversaryEdition {
  edition_year: number;
  total: number;
  invited: number;
  foreword_written: number;
  generating: number;
  delivered: number;
  declined: number;
  oldest_open_invite_at: string | null;
}

export interface AnniversaryPipeline {
  invite_due_now: number;
  upcoming_90d: number;
  oldest_due_wedding_date: string | null;
  edition_year: number;
  interval: string;
}

/**
 * `available: false` IS NOT `unavailable[]`. It means migration 451 is not on
 * this database, so the programme cannot be measured here at all — an
 * explanatory caption, and NO Retry button: retrying cannot create a table.
 */
export interface AnniversaryProgramme {
  available: boolean;
  reason?: string;
  opt_in: AnniversaryOptIn | null;
  editions: AnniversaryEdition[];
  pipeline: AnniversaryPipeline | null;
  oldest_open_invite_at: string | null;
  oldest_open_invite_days: number | null;
  denominator_tiers: string[];
  statuses: string[];
  opt_out_tracked?: boolean;
  /** "counts are eligibility, not scheduled sends" — render it verbatim. */
  eligibility_note: string;
}

// ── E4 · reminder pressure ──────────────────────────────────────────────────

/**
 * What the reminder engine currently has SCHEDULED. Never "will send".
 *
 * ⚠️ `due_7d` INCLUDES `due_24h`. They are "inside the next N", not disjoint
 * bands, and adding them double-counts every guest in the first day.
 *
 * `definition.note` explains that the engine re-checks each guest inside the
 * transaction that claims them, so a contributor who submits their chapter
 * first is counted here and never written to. A UI must render that note.
 */
export interface ReminderPressure {
  scope: string;
  scheduled_eligible: number;
  overdue_now: number;
  due_24h: number;
  due_7d: number;
  at_ceiling: number;
  deadline_pass_pending: number;
  oldest_overdue_at: string | null;
  max_reminders: number;
  definition: Record<string, unknown>;
}

// ── O4 · per-section degradation ────────────────────────────────────────────

/**
 * One aggregate that FAILED on this request.
 *
 * `reason` is a fixed generic sentence, never a driver message — the real error
 * is on the server's log line and nowhere else. An empty array is the HEALTHY
 * state and must be treated as such, never as "unknown".
 *
 * This is a different thing from a section's own `available: false`: a section
 * named here might well work on the next attempt, so it gets a Retry. A section
 * that says `available: false` is describing a table this database does not
 * have, and a Retry there would be a button that cannot possibly help.
 */
export interface UnavailableSection {
  key: string;
  reason: string;
}

export interface WeddingOverview {
  // EVERY ONE OF THESE CAN BE NULL. The server settles its aggregates
  // individually (O4), so one failed query ships its own key as null and names
  // itself in `unavailable` rather than taking the whole page down. Nullable
  // rather than optional: the key is always present, its value may be absent.
  books: BooksByStatus | null;
  revenue: RevenueTotals | null;
  tier_mix: TierMixRow[] | null;
  participation: ParticipationTotals | null;
  conversions: ConversionFunnel | null;
  engagement: EngagementTotals | null;
  recent_activity: ActivityEvent[] | null;
  generated_at: string;

  // Range-scoped additions. OPTIONAL on the type on purpose: a dashboard bundle
  // deployed against a backend that predates them must render the old cards
  // rather than crash on a missing key. Nullable too, for the O4 reason above.
  range?: RangeMeta | null;
  kpis?: OverviewKpis | null;
  revenue_in_range?: RevenueInRange | null;
  newest_couples?: NewestCoupleRow[] | null;
  acquisition_source_available?: boolean;
  acquisition_source_reason?: string | null;
  invitation_funnel?: InvitationFunnel | null;
  template_usage?: TemplateUsage | null;
  email_templates_summary?: EmailTemplatesSummary | null;

  // Wave-2 additions (E3, E4, Q1, Q3). Same rule.
  anniversary?: AnniversaryProgramme | null;
  reminder_pressure?: ReminderPressure | null;
  writing_quality?: WritingQuality | null;
  voice_quota?: VoiceQuota | null;

  /** Which sections failed, and nothing about why. `[]` is healthy. */
  unavailable?: UnavailableSection[];
}

/** `?range=30d` or `?range=custom&from=…&to=…` — what TimeRangeFilter speaks. */
export interface RangeQuery {
  range?: string;
  from?: string;
  to?: string;
}

export interface BookRow {
  id: string;
  wedding_code: string;
  partner1_name: string;
  partner2_name: string;
  tier: Tier;
  status: BookStatus;
  wedding_date: string | null;
  couple_timezone: string;
  contribution_deadline: string | null;
  max_contributors: number;
  extra_contributor_count: number;
  created_at: string;
  couple_email: string;
  total_guests: Numeric;
  invited_guests: Numeric;
  submitted_guests: Numeric;
  revenue_cents: Numeric;
  paid_purchases: Numeric;

  // Row additions. Optional for the same reason as the overview keys above.
  photos?: Numeric;
  invites_sent?: Numeric;
  last_invite_at?: string | null;
  /** NULL for a book that has invited nobody: no denominator, no rate. 0–1. */
  participation?: number | null;
  at_risk?: boolean;

  /**
   * E4. Reminders the engine has SCHEDULED for this book right now — never
   * "will send", and 0 rather than null for a book whose status or deadline
   * makes it ineligible (the honest answer to "how many are scheduled here" is
   * none, not unknown).
   */
  reminders_due?: number;
  /** Guests who have had all three nudges and will get no more. */
  guests_at_reminder_ceiling?: number;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

/** Counts for the filter pills. Scoped to tier+search only, never to status. */
export interface BooksStatusCounts {
  all: number;
  collecting: number;
  at_risk: number;
  closed: number;
}

export interface BooksKpis {
  books: number;
  /** 0–1, or NULL when no book in scope has invited anybody. */
  median_participation: number | null;
  at_risk: number;
  closing_this_week: number;
  avg_revenue_cents: Numeric | null;
  avg_revenue_cents_paid_books: Numeric | null;
  revenue_cents: Numeric;
}

/** The server's ONE definition of "at risk" — the grid never recomputes it. */
export interface AtRiskDefinition {
  statuses: string[];
  deadline_within_days: number;
  participation_below: number;
  requires_invited: boolean;
}

export interface BooksResponse {
  books: BookRow[];
  pagination: Pagination;
  status_counts?: BooksStatusCounts;
  kpis?: BooksKpis;
  applied_filters?: {
    status: string | null;
    tier: string | null;
    search: string | null;
    at_risk_only: boolean;
  };
  at_risk_definition?: AtRiskDefinition;
  /**
   * The SAME definition object /overview ships with `reminder_pressure`, so the
   * grid column and the tile cannot describe one number two ways. `measured` is
   * 'scheduled' — a column header must never say "will send".
   */
  reminders_due_definition?: Record<string, unknown>;
  sortable_columns?: string[];
}

export interface BookDetailBook extends BookRow {
  couple_id: string;
  couple_email_verified: boolean;
  couple_last_login_at: string | null;
  couple_created_at: string;
  welcome_message: string | null;
  style_key: string | null;
  options: Record<string, unknown>;
  pdf_s3_key: string | null;
  generated_at: string | null;
  delivered_at: string | null;
  updated_at: string;
}

export interface GuestStats {
  total: number; pending: number; invited_only: number;
  verified: number; writing: number; submitted: number; invited: number;
}

export interface ChapterStats {
  chapters: number; visible_chapters: number; hidden_chapters: number;
  draft_chapters: number; total_words: number;
}

export interface MediaStats {
  photos: number; voice_recordings: number; voice_seconds: number;
}

/**
 * WB-VOICE (session O, migration 448). A QUOTA NUMBER, not a vanity metric.
 *
 * Resemble's rapid-clone quota is 500 ACCOUNT-WIDE and paying members draw on
 * the same pool. A 300-guest Platinum wedding can take most of it and two of
 * them cannot coexist, so an operator needs to see this per book BEFORE the
 * second Platinum sale, not after the support ticket.
 *
 * `voice_profiles` counts LIVE profiles only — a deleted one has released its
 * slot. Optional on the type because a dashboard bundle deployed against a
 * backend that predates 448 must not crash on a missing key.
 */
export interface VoiceStats {
  voice_profiles: Numeric;
  voice_profiles_ready: Numeric;
  voice_profiles_pending: Numeric;
  voice_profiles_failed: Numeric;
}

export interface Purchase {
  id: string;
  purchase_type: 'tier' | 'upgrade' | 'extra_contributors';
  tier: Tier | null;
  quantity: number;
  amount_cents: Numeric;
  currency: string;
  coupon_code: string | null;
  status: 'pending' | 'paid' | 'refunded' | 'failed';
  purchased_at: string | null;
  refunded_at: string | null;
  created_at: string;
  stripe_payment_intent_id: string | null;
  stripe_checkout_session_id: string | null;
}

export interface BookDetail {
  book: BookDetailBook;
  guest_stats: GuestStats;
  chapter_stats: ChapterStats;
  media_stats: MediaStats;
  purchases: Purchase[];
  revenue_cents: number;
  conversions: ConversionFunnel;
  voice_stats?: VoiceStats;
}

export interface GuestRow {
  id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  relationship: string | null;
  role: string | null;
  guest_code: string;
  status: GuestStatus;
  invited_at: string | null;
  last_activity_at: string | null;
  reminder_count: number;
  next_reminder_at: string | null;
  created_at: string;
  chapter_id: string | null;
  chapter_status: string | null;
  /**
   * How many Chapters this guest has in total. The chapter_* columns beside it
   * describe the FIRST one only (the guests query pins `chapter_index = 0`),
   * because a Platinum Contributor may hold a second one. Greater than 1 is
   * therefore the Platinum signal — no tier field is needed to read it.
   */
  chapter_count: Numeric;
  content_type: string | null;
  word_count: number | null;
  submitted_at: string | null;
  emails_sent: Numeric;
  last_sent_at: string | null;
  ever_opened: boolean;
  ever_clicked: boolean;
  ever_bounced: boolean;

  /**
   * Q3 — the voice-clone consent record, four columns from migration 448.
   *
   * ALL FOUR ARE NULL FOR A GUEST WHO NEVER CONSENTED, and NULL is the right
   * zero state: render it as a dash, never as "no". A contributor who was never
   * offered a voice clone has not declined one. Optional on the type per the
   * stale-bundle rule.
   */
  voice_status?: string | null;
  voice_consent_at?: string | null;
  voice_consent_text_version?: string | null;
  voice_sample_seconds?: number | null;
}

/**
 * Whether anything on this database WRITES per-guest opens/bounces.
 *
 * A property of the database, not of a guest: the server reads its own
 * reconciliation sync state, so these flip to true on their own once the
 * stitch job runs. OPTIONAL, per the stale-bundle rule — a bundle deployed
 * against a backend that predates them must render rather than crash, and the
 * consumers therefore treat `undefined` as "not tracked".
 */
export interface EngagementTracking {
  opened_tracked?: boolean;
  bounced_tracked?: boolean;
  tracking_reason?: string | null;
}

export interface GuestsResponse {
  guests: GuestRow[];
  max_reminders: number;
  engagement_tracking?: EngagementTracking;
}

export interface RevenueTrendPoint {
  bucket: string;
  purchase_type: string;
  cents: Numeric;
  purchases: number;
}

/**
 * A prior-period bucket, shifted forward onto the current period's axis.
 *
 * `aligned_bucket` is the key to plot against — prior week 1 lands on top of
 * current week 1 however the buckets fall. `bucket` is where it really was.
 */
export interface RevenuePriorTrendPoint {
  aligned_bucket: string;
  bucket: string;
  cents: Numeric;
  purchases: number;
}

export interface RevenueAttach {
  books_with_extras: number;
  books_paid: number;
  books_upgraded: number;
}

export interface RevenueTotalsBlock {
  cents: Numeric;
  purchases: number;
  books: number;
}

/**
 * Do guests who recorded their voice convert better than text-only ones?
 *
 * A REAL number or nothing. `multiple` is NULL when no text-only guest has
 * converted — there is nothing to be a multiple of — and `sample_size` says how
 * many guests the two rates were measured across. The prototype's "2.3×" is an
 * illustration and must never be rendered.
 */
export interface VoiceInsight {
  scope: string;
  voice_guests: Numeric;
  text_guests: Numeric;
  voice_converted: Numeric;
  text_converted: Numeric;
  voice_rate: number | null;
  text_rate: number | null;
  multiple: number | null;
  sample_size: number;
  note: string;
}

/**
 * E2 — refunds, net, and the denominator that is the whole point.
 *
 * `refund_rate_all_time` divides by `ever_settled_count`, which is
 * `status IN ('paid','refunded')` — NEVER 'paid' alone. wedding_purchases.status
 * is a single column, so refunding a purchase MOVES the row out of 'paid': a
 * rate of refunded/paid divides by a denominator the numerator has already been
 * subtracted from. NULL when nothing has ever settled.
 *
 * `net_cents_in_range` and `prior_net_cents` are computed SERVER-side and can be
 * negative — a refund issued this week for a purchase made last quarter
 * subtracts from a window it never added to. No screen should redo this
 * arithmetic; that is how two pages come to disagree about one window.
 *
 * `refunded_without_timestamp` is the size of a blind spot: refunded_at is
 * nullable, and such a row appears in NO window here, only in the all-time
 * totals.
 */
export interface RevenueRefunds {
  cents_in_range: Numeric;
  count_in_range: number;
  prior_cents: Numeric;
  prior_count: number;
  delta_pct: number | null;
  net_cents_in_range: number;
  prior_net_cents: number;
  gross_cents_in_range: Numeric;
  refunded_cents_all_time: Numeric;
  refunded_count_all_time: number;
  ever_settled_count: number;
  ever_settled_cents: Numeric;
  refund_rate_all_time: number | null;
  refunded_without_timestamp: number;
  bucketed_by: string;
  rate_denominator: string;
  basis_note: string;
}

export interface RevenueResponse {
  bucket: 'day' | 'week' | 'month';
  range: { start: string; end: string };
  trend: RevenueTrendPoint[];
  tier_mix: TierMixRow[];
  /** ALL TIME on purpose — see `attach_in_range` for the windowed answer. */
  attach: RevenueAttach;
  totals: RevenueTotalsBlock;

  range_meta?: RangeMeta | null;
  prior_range?: { start: string; end: string };
  prior_trend?: RevenuePriorTrendPoint[];
  best_bucket?: { bucket: string; cents: Numeric } | null;
  attach_scope?: 'all_time';
  attach_in_range?: RevenueAttach;
  prior_totals?: RevenueTotalsBlock;
  voice_insight?: VoiceInsight;
  refunds?: RevenueRefunds;
}

export interface UtmRow {
  source: string;
  campaign: string;
  touched: number;
  clicked: number;
  converted: number;
}

export interface RecentConversion {
  id: string;
  book_id: string;
  guest_id: string | null;
  cta_clicked_at: string | null;
  converted_at: string | null;
  lifescribe_user_id: string | null;
  utm: Record<string, unknown>;
  wedding_code: string;
  first_name: string | null;
  last_name: string | null;
}

export interface ConversionsResponse {
  funnel: ConversionFunnel;
  by_utm: UtmRow[];
  recent: RecentConversion[];
}

export interface SuspendResult {
  book: { id: string; status: BookStatus; updated_at: string };
  previous_status: BookStatus;
}

export interface ReactivateResult {
  book: { id: string; status: BookStatus; updated_at: string };
  restored_status: BookStatus;
}

export interface DeadlineResult {
  book_id: string;
  contribution_deadline: string;
  couple_timezone: string;
  previous_deadline: string | null;
}

export interface ResendResult {
  sent: boolean;
  action: 'invitation' | 'reminder' | null;
  reminder_count: number | null;
  max_reminders: number;
  next_reminder_at: string | null;
}

export interface BooksQuery {
  page?: number;
  limit?: number;
  status?: string;
  tier?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// ── Catalogue ───────────────────────────────────────────────────────────────

export type CatalogueKind = 'style' | 'edition' | 'layout';

/**
 * One offered style, edition or dashboard layout.
 *
 * `print_css` / `web_css` NULL means "use the appearance compiled into the pdf
 * image and the web bundle" — which is how all fifteen built-ins render. It is
 * not a missing value.
 */
export interface CatalogueItem {
  id: string;
  kind: CatalogueKind;
  item_key: string;
  name: string;
  description: string;
  sort_order: number;
  is_active: boolean;
  is_builtin: boolean;
  palette_primary: string | null;
  palette_accent: string | null;
  palette_paper: string | null;
  signature: string[];
  cover_theme: Record<string, unknown> | null;
  print_css: string | null;
  web_css: string | null;
  created_at: string;
  updated_at: string;
  created_by_admin: string | null;
  updated_by_admin: string | null;
  tiers: string[];
  books_using: Numeric;
  couples_using: Numeric;
}

export interface CatalogueResponse {
  items: CatalogueItem[];
  /** Bumped in the same transaction as every write; service replicas poll it. */
  version: number | null;
  version_updated_at: string | null;
  defaults: Record<CatalogueKind, string>;
  tiers: Tier[];
}

export interface CatalogueWriteResult {
  item: CatalogueItem;
  version: number | null;
  /** Set when the item will render unornamented because it has no stylesheet. */
  appearance_note?: string | null;
}

// ── Guest conversions ───────────────────────────────────────────────────────

/**
 * One converted person.
 *
 * `plan_name` is where the member is NOW, not what the wedding funnel sold
 * them: at conversion the plan is always 'trial', and any paid plan arrives
 * later outside the wedding tables. The response carries
 * `plan_is_point_in_time: true` so the page cannot render the column without
 * the caveat.
 */
export interface ConvertedGuest {
  conversion_id: string;
  guest_id: string | null;
  book_id: string;
  converted_via: 'offer_shown' | 'auto_provision' | null;
  matched_existing: boolean;
  offered_at: string | null;
  consented_at: string | null;
  converted_at: string | null;
  cta_clicked_at: string | null;
  lifescribe_user_id: string | null;
  campaign: string | null;
  /** 'web' | 'app' — the WB-13 channel tag, written since 453 but never read until now. */
  channel: string | null;
  wedding_code: string;
  partner1_name: string;
  partner2_name: string;
  first_name: string | null;
  last_name: string | null;
  guest_email: string | null;
  user_id: string | null;
  user_created_at: string | null;
  plan_code: string | null;
  subscription_status: string | null;
  plan_name: string | null;
  display_tier: string | null;
  billing_cycle: string | null;
  /** True when only the email fallback found them — matched_existing rows carry no user id. */
  matched_by_email: boolean;
  ever_paid: boolean;
}

export interface ConversionPlanRollup {
  plan_code: string;
  plan_name: string;
  display_tier: string | null;
  billing_cycle: string | null;
  guests: Numeric;
  active: Numeric;
  ever_paid: Numeric;
}

export interface ConversionChannelRollup {
  channel: string;
  guests: Numeric;
  new_members: Numeric;
  ever_paid: Numeric;
}

export interface ConvertedGuestsResponse {
  guests: ConvertedGuest[];
  by_plan: ConversionPlanRollup[];
  by_channel: ConversionChannelRollup[];
  pagination: Pagination;
  plan_is_point_in_time: boolean;
}

export interface ConvertedGuestsQuery {
  page?: number;
  limit?: number;
  channel?: 'web' | 'app' | 'all';
  matched?: 'matched' | 'new' | 'all';
  plan?: string;
}

// ── Couples ─────────────────────────────────────────────────────────────────
//
// Every count and money field is `Numeric` because node-pg returns bigint and
// numeric as strings — read them through `num()` from weddingFormat, never as
// numbers directly.

export interface CoupleRow {
  id: string;
  email: string;
  partner1_name: string;
  partner2_name: string;
  email_verified: boolean;
  last_login_at: string | null;
  created_at: string;
  failed_login_attempts: Numeric;
  lockout_until: string | null;
  locked_out: boolean;
  reset_pending: boolean;
  books: Numeric;
  revenue_cents: Numeric;
  top_tier: Tier | null;
}

export interface CouplesResponse {
  couples: CoupleRow[];
  pagination: Pagination;
}

export interface CouplesQuery {
  page?: number;
  limit?: number;
  search?: string;
  verified?: 'all' | 'verified' | 'unverified';
  locked?: 'all' | 'locked' | 'unlocked';
  tier?: Tier | 'all';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/** A book as the couple detail dialog sees it — carries what the tier dialog needs. */
export interface CoupleBookRow {
  id: string;
  wedding_code: string;
  partner1_name: string;
  partner2_name: string;
  tier: Tier;
  status: BookStatus;
  wedding_date: string | null;
  couple_timezone: string;
  contribution_deadline: string | null;
  max_contributors: Numeric;
  extra_contributor_count: Numeric;
  options: Record<string, boolean> | null;
  created_at: string;
  live_guests: Numeric;
  revenue_cents: Numeric;
}

/**
 * Money paid through a Stripe Payment Link that was never redeemed. It appears
 * in NO revenue figure anywhere in this dashboard — a claim only becomes a
 * wedding_purchases row when the couple claims it — so this is the one surface
 * where "they paid and nothing happened" is visible.
 */
export interface CoupleClaim {
  id: string;
  tier: Tier;
  amount_cents: Numeric;
  currency: string;
  /**
   * The EFFECTIVE status, evaluated at read time — see ClaimRow below. It
   * replaces the stored column under this key on purpose: this card was
   * counting lapsed claims as money the couple could still redeem, because
   * expiry is applied lazily and such a row still reads 'pending_claim'.
   */
  status: 'pending_claim' | 'claimed' | 'expired' | 'refunded';
  /** The untouched column, projected beside it. Optional per the stale-bundle rule. */
  stored_status?: 'pending_claim' | 'claimed' | 'expired' | 'refunded';
  created_at: string;
  claim_token_expires_at: string | null;
  claimed_at: string | null;
}

export interface TierSpecEntry {
  rank: number;
  name: string;
  price_cents: number;
  max_contributors: number;
  voice_enabled: boolean;
  ai_enhancement_enabled: boolean;
  custom_cover: boolean;
  hi_res_pdf: boolean;
  video_enabled: boolean;
}

export interface CoupleDetail {
  couple: CoupleRow & {
    updated_at: string;
    reset_expires_at: string | null;
    verify_expires_at: string | null;
  };
  books: CoupleBookRow[];
  purchases: (Purchase & { wedding_code: string })[];
  sessions: { active_sessions: Numeric; last_session_at: string | null };
  /** Filtered on the EFFECTIVE status, so a lapsed claim is not listed here. */
  unredeemed_claims: CoupleClaim[];
  claims: CoupleClaim[];
  /** Why a claim can read 'expired' while its stored column says otherwise. */
  claims_expiry_note?: string;
  tier_spec: Record<Tier, TierSpecEntry>;
}

export interface TierChangeResult {
  book: CoupleBookRow;
  previous_tier: Tier;
  direction: 'up' | 'down';
  live_guests: Numeric;
  new_cap: Numeric;
  over_cap: boolean;
  activated: boolean;
  amount_charged_cents: number;
  refund_handled_externally: boolean;
  dashboard_cache_note: string;
}

export interface CoupleAuthActionResult {
  requested: boolean;
  email_masked: string;
  email_verified: boolean;
}

// ── E1 · purchase claims ────────────────────────────────────────────────────
//
// MONEY TAKEN THROUGH A STRIPE PAYMENT LINK THAT WAS NEVER REDEEMED. A claim
// only becomes a wedding_purchases row when the couple claims it, so none of
// this appears in any revenue figure anywhere in this dashboard.
//
// EXPIRY IS LAZY AND ADMIN NEVER WRITES. Migration 446 flips a lapsed claim to
// 'expired' the next time the wedding service looks at it — there is no sweep.
// So every row carries BOTH the untouched column (`stored_status`) and the
// status evaluated at read time (`status`), plus `expired_by_time` saying the
// two differ. A row can therefore read `stored_status: 'pending_claim'` with
// `status: 'expired'`; that is correct, not a bug, and the screen must show the
// effective status without ever issuing the UPDATE itself.
//
// `claim_token_hash` is deliberately NOT projected by the server and must never
// be asked for: it is the credential that redeems the purchase.

export type ClaimStatus = 'pending_claim' | 'claimed' | 'expired' | 'refunded';

export interface ClaimRow {
  id: string;
  stripe_checkout_session_id: string | null;
  stripe_payment_intent_id: string | null;
  tier: Tier;
  amount_cents: Numeric;
  currency: string;
  buyer_email: string | null;
  buyer_name: string | null;
  /** The untouched column. May disagree with `status` — see above. */
  stored_status: ClaimStatus;
  /** Evaluated at read time. THIS is what the grid renders. */
  status: ClaimStatus;
  expired_by_time: boolean;
  claim_token_expires_at: string | null;
  claimed_at: string | null;
  claimed_book_id: string | null;
  purchase_id: string | null;
  created_at: string;
  updated_at: string;
  age_days: number;
  // LEFT JOINed book — null for an unclaimed row, which is most of them.
  wedding_code: string | null;
  partner1_name: string | null;
  partner2_name: string | null;
  book_status: BookStatus | null;
}

/** Pill counts. Scoped to tier + search only, never to status. */
export interface ClaimStatusCounts {
  all: number;
  pending_claim: number;
  claimed: number;
  expired: number;
  refunded: number;
}

export interface ClaimAgingBucket {
  key: string;
  label: string;
  count: number;
  cents: Numeric;
}

export interface ClaimsSummary {
  total_claims: number;
  total_cents: Numeric;
  by_status: Record<ClaimStatus, { count: number; cents: Numeric }>;
  /** Buckets cover EFFECTIVELY-PENDING claims only. */
  aging: { scope: string; buckets: ClaimAgingBucket[]; note: string };
  oldest_pending_at: string | null;
  /** NULL, not 0, when nothing is pending. */
  oldest_pending_days: number | null;
  /** 'all_time' — the whole table, which a tier filter must not quietly shrink. */
  scope: string;
}

export interface ClaimsResponse {
  available: boolean;
  reason?: string;
  claims: ClaimRow[];
  pagination: Pagination;
  status_counts: ClaimStatusCounts | null;
  summary: ClaimsSummary | null;
  applied_filters: { tier: string | null; status: string | null; search: string | null };
  sortable_columns: string[];
  statuses?: ClaimStatus[];
  expiry_note: string;
  /** Always false. This endpoint reads; the wedding service owns the transition. */
  writes?: false;
  generated_at: string;
}

export interface ClaimsQuery {
  page?: number;
  limit?: number;
  tier?: Tier;
  status?: ClaimStatus;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// ── Q2 · media inventory (READ-ONLY) ────────────────────────────────────────
//
// TWO HONESTY NOTES SHIP WITH EVERY RESPONSE AND BOTH MATTER.
//
// A photo or an audio row reading 'pending' IS NOT AWAITING REVIEW: migration
// 450 added the column with DEFAULT 'pending' across every existing row, and
// nothing in the product filters photos or audio on it. Those files are already
// in the couple's book. VIDEO is the one type the loader does gate, out of the
// preview and out of the PDF — a pending video is genuinely unseen.
//
// There is no approve/hide route in this wave and NO poster_key is projected:
// admin-backend has no signing path for a wedding media key, and approving a
// clip nobody watched is the exact failure 450 argues against.

export type MediaModerationState = 'pending' | 'approved' | 'hidden';

export type MediaType = 'photo' | 'audio' | 'video';

export interface MediaRow {
  id: string;
  media_type: MediaType;
  moderation: MediaModerationState;
  moderated_at: string | null;
  upload_status: string;
  duration_seconds: number | null;
  /** BIGINT — a STRING off the wire. Read it through num() before arithmetic. */
  size_bytes: Numeric | null;
  mime_type: string | null;
  sort_order: number | null;
  created_at: string;
  chapter_id: string;
  chapter_status: string | null;
  chapter_index: number;
  word_count: number | null;
  chapter_submitted_at: string | null;
  guest_id: string;
  first_name: string;
  last_name: string | null;
  guest_email: string;
  relationship: string | null;
  book_id: string;
  wedding_code: string;
  partner1_name: string;
  partner2_name: string;
  tier: Tier;
  book_status: BookStatus;
  couple_email: string;
}

/** media_type × moderation, over every uploaded/processed row — filters ignored. */
export interface MediaCountRow {
  media_type: MediaType;
  moderation: MediaModerationState;
  n: number;
}

export interface MediaResponse {
  media: MediaRow[];
  pagination: Pagination;
  counts: MediaCountRow[];
  applied_filters: { state: string | null; type: string | null };
  defaults: { state: string; type: string };
  states: MediaModerationState[];
  types: MediaType[];
  read_only: boolean;
  notes: { structural_pending: string; video_gate: string; read_only: string };
  upload_status_filter: string[];
  upload_status_note: string;
  generated_at: string;
}

export interface MediaQuery {
  page?: number;
  limit?: number;
  /** Omit for the DEFAULT view (pending video). 'all' means no filter. */
  state?: string;
  type?: string;
}

// ── Members ─────────────────────────────────────────────────────────────────
//
// Three populations on one screen: couples, coordinators and converted guests.
// COORDINATORS ARE NOT IN PRODUCTION. `wedding_coordinators` is migration 459
// and lives only in the sandbox, so the server answers `{available:false}` with
// a reason and every count NULL. The page must say "not yet in production"
// rather than render a confident zero.

export interface CoordinatorKpi {
  available: boolean;
  reason?: string;
  invited_total: Numeric | null;
  accepted: Numeric | null;
  accepted_in_range: Numeric | null;
  accepted_in_prev_range?: Numeric | null;
  /** 0–1. */
  accept_rate: number | null;
  delta_pct: number | null;
}

export interface MembersKpis {
  members_touched: KpiBlock & { includes_coordinators: boolean };
  couples: KpiBlock & { total: Numeric };
  coordinators: CoordinatorKpi;
  guests_joined: KpiBlock & { total: Numeric };
}

export interface FirstTouchSource {
  source: string;
  books: Numeric;
  books_in_range: Numeric;
}

/**
 * E5 — one wedding planner who has sent LifeScribe business.
 *
 * `planner` is the folded key (`lower(trim(…))`); `planner_as_written` is what
 * a couple actually typed. Two spellings of one firm are TWO ROWS — this is a
 * lead list, not a ledger, and the server's `note` says exactly that.
 */
export interface PlannerReferralRow {
  planner: string;
  planner_as_written: string;
  books: number;
  books_in_range: number;
  paid_cents: Numeric;
  latest_book_at: string;
}

/**
 * Its own availability flag: this needs BOTH 1111 columns, so first_touch can
 * be live while this is not. An empty `planners` list is the DAY-ONE state —
 * nothing writes acquisition_detail until the setup wizard's "how did you hear
 * about us" step ships — so the zero state must look intentional.
 */
export interface PlannerReferrals {
  available: boolean;
  reason?: string;
  planners: PlannerReferralRow[];
  total_named?: number;
  limit?: number;
  source_key?: string;
  note: string;
}

export interface MembersFirstTouch {
  available: boolean;
  reason?: string;
  sources: FirstTouchSource[];
  planner_referrals?: PlannerReferrals;
}

export interface MembersConversionUtmRow {
  source: string;
  campaign: string;
  touched: Numeric;
  converted: Numeric;
}

export interface MembersEngagement {
  wrote_chapter: Numeric;
  added_photos: Numeric;
  made_voice_recording: Numeric;
  started_own_book: {
    available: boolean;
    reason?: string;
    value: Numeric | null;
    scope?: string;
    deleted_column_present?: boolean;
  };
  scope: string;
  note: string;
}

/**
 * Q4 — three numbers that are all POINT IN TIME and none of which can be
 * range-scoped: a lockout is a state that exists right now and expires on its
 * own, and nothing records the event. Tiles must be labelled accordingly; a
 * friction number sitting under a 7-day pill that silently ignores it is the
 * same class of lie as a structural zero.
 *
 * `guests_locked_now` counts DISTINCT GUESTS, not session rows — one person
 * fighting a login on a phone and a laptop is two locked sessions and exactly
 * one person having a bad time.
 */
export interface MembersAuthFriction {
  scope: string;
  couples_locked_now: number;
  guests_locked_now: number;
  quiet_couples: number;
  /** A SUBSET of `quiet_couples`, not a figure to add beside it. */
  quiet_couples_never_logged_in: number;
  quiet_after_days: number;
  definition: Record<string, unknown>;
}

export interface MembersResponse {
  range: RangeMeta;
  kpis: MembersKpis;
  first_touch: MembersFirstTouch;
  conversion_utm: MembersConversionUtmRow[];
  engagement: MembersEngagement;
  coordinators_available: boolean;
  auth_friction?: MembersAuthFriction;
  generated_at: string;
}

export type MemberRole = 'couple' | 'coordinator' | 'member';

export interface MemberRow {
  role: MemberRole;
  id: string;
  name: string | null;
  email: string | null;
  /** 'instagram' | 'SM2027 invite' | 'SM2027 cross-sell' | null. */
  came_from: string | null;
  came_from_kind: 'acquisition_source' | 'invite' | 'cross_sell' | 'unknown';
  books: Numeric;
  status: string;
  joined_at: string | null;
  wedding_code: string | null;
}

export interface MembersListResponse {
  members: MemberRow[];
  pagination: Pagination;
  role_counts: { role: MemberRole; n: Numeric }[];
  coordinators_available: boolean;
  coordinators_reason: string | null;
  acquisition_source_available: boolean;
  scope: 'range' | 'all_time';
  range: RangeMeta;
  sortable_columns: string[];
  roles: MemberRole[];
}

export interface MembersListQuery extends RangeQuery {
  page?: number;
  limit?: number;
  role?: MemberRole;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  /** Omit for "who has ever joined"; 'range' intersects with the time filter. */
  scope?: 'range';
}

// ── Email template registry ─────────────────────────────────────────────────
//
// Versions-as-rows: one live version per family, at most one open draft, and a
// published row is NEVER updated. Publishing makes a template SELECTABLE in
// every coordinator dashboard — it does not send or schedule any email, which
// is why the server returns `sends_email: false` for the toast to stand on.
//
// STATS ARE PROVIDER-LEVEL AND USUALLY NULL. No send path tags registry keys
// yet, so `delivered`/`unique_opens`/`unique_clicks` come back NULL and must
// render as '—'. A zero would claim a measured nothing.

export type TemplateAudience =
  | 'guests' | 'quiet_guests' | 'coordinators' | 'couple' | 'guests_platinum';

export type TemplateKind = 'invitation' | 'reminder' | 'thank_you';

export type TemplateStatus = 'draft' | 'live' | 'superseded' | 'retired';

/** One row of the registry — a single VERSION of a template family. */
export interface TemplateVersion {
  id: string;
  registry_key: string;
  version: number;
  name: string;
  audience: TemplateAudience;
  kind: TemplateKind;
  subject: string;
  body: string;
  status: TemplateStatus;
  created_by_admin: string | null;
  created_at: string;
  updated_at: string;
  published_by_admin: string | null;
  published_at: string | null;
  superseded_at: string | null;
  superseded_by: string | null;
}

/** One template FAMILY: its latest version, its live one, its open draft. */
export interface TemplateFamily {
  registry_key: string;
  name: string;
  audience: TemplateAudience;
  kind: TemplateKind;
  latest_id: string;
  latest_version: number;
  latest_status: TemplateStatus;
  latest_subject: string;
  latest_updated_at: string;
  live_id: string | null;
  live_version: number | null;
  live_name: string | null;
  live_subject: string | null;
  live_audience: TemplateAudience | null;
  live_kind: TemplateKind | null;
  live_published_at: string | null;
  live_published_by_admin: string | null;
  draft_id: string | null;
  draft_version: number | null;
  draft_updated_at: string | null;
  versions: Numeric;
  first_created_at: string;
  last_touched_at: string;
  delivered: Numeric | null;
  unique_opens: Numeric | null;
  unique_clicks: Numeric | null;
}

export interface TemplateContract {
  audiences: TemplateAudience[];
  kinds: TemplateKind[];
  statuses: TemplateStatus[];
  /** Read these from the payload — never hardcode the merge-field list. */
  merge_fields: string[];
  subject_max: number;
  subject_soft_max: number;
  name_max: number;
  body_max: number;
}

export interface TemplatesResponse extends TemplateContract {
  available: boolean;
  not_migrated_reason?: string;
  templates: TemplateFamily[];
  counts: { keys: number; live: number; draft: number; superseded: number; retired: number };
  stats_available: boolean;
  stats_note?: string;
  book_count: number | null;
  generated_at?: string;
}

export interface TemplateWriteBody {
  name?: string;
  subject?: string;
  body?: string;
  audience?: TemplateAudience;
  kind?: TemplateKind;
}

export interface TemplateCreateResult {
  template: TemplateVersion;
  published: false;
  publish_note: string;
}

export interface TemplatePublishResult {
  template: TemplateVersion;
  superseded: { id: string; version: number } | null;
  live_count: number;
  book_count: number | null;
  /** Always false. Publishing is a visibility change, not a send. */
  sends_email: false;
}

export interface TemplateNewVersionResult {
  template: TemplateVersion;
  copied_from_version: number;
}

export interface TemplateRetireResult {
  template: TemplateVersion;
  live_count: number;
  existing_copies_note: string;
}

export interface TemplateHistoryResponse {
  available: boolean;
  not_migrated_reason?: string;
  registry_key: string;
  versions: TemplateVersion[];
  live_version?: number | null;
  draft_version?: number | null;
}

// ── Client ──────────────────────────────────────────────────────────────────

export const weddingApi = {
  getOverview: (params: RangeQuery = {}) =>
    request<WeddingOverview>(`/overview${qs(params as Record<string, string | undefined>)}`),

  /**
   * The latest activity across every book, at a caller-chosen depth.
   *
   * A SEPARATE, SMALL ENDPOINT so the live feed can poll every few seconds
   * without re-running the eighteen aggregates behind /overview. It is NOT
   * scoped to the page's date filter and the payload's `note` says so — render
   * that string rather than writing the caption locally, or the two drift.
   *
   * The server clamps `limit` to 1..100.
   */
  getActivity: (limit = 20) => request<ActivityResponse>(`/activity${qs({ limit })}`),

  getBooks: (params: BooksQuery = {}) =>
    request<BooksResponse>(`/books${qs(params as Record<string, string | number | undefined>)}`),

  getBook: (bookId: string) => request<BookDetail>(`/books/${encodeURIComponent(bookId)}`),

  getBookGuests: (bookId: string) =>
    request<GuestsResponse>(`/books/${encodeURIComponent(bookId)}/guests`),

  getBookActivity: (bookId: string, limit = 50) =>
    request<{ events: ActivityEvent[] }>(
      `/books/${encodeURIComponent(bookId)}/activity${qs({ limit })}`
    ),

  /**
   * TWO WIRE FORMATS, and the server prefers the first.
   *
   * `start`/`end` are local-midnight ISO instants — what this page has always
   * sent, and what keeps `bucketFor()` honest. `range`/`from`/`to` (the shared
   * TimeRangeFilter vocabulary) are read ONLY when both start and end are
   * absent. Send one or the other, not a contradictory mixture.
   */
  getRevenue: (
    params: { start?: string; end?: string; bucket?: string } & RangeQuery = {}
  ) => request<RevenueResponse>(`/revenue${qs(params)}`),

  getConversions: () => request<ConversionsResponse>('/conversions'),

  /**
   * Unclaimed purchases — aggregates and the paginated list in ONE response.
   *
   * `available: false` means wedding_purchase_claims is not on this database;
   * the arrays are empty and `summary` is null, and the page must print the
   * reason rather than a confident zero. Read `status`, not `stored_status`,
   * for anything an operator acts on: see ClaimRow.
   */
  getClaims: (params: ClaimsQuery = {}) =>
    request<ClaimsResponse>(`/claims${qs(params as Record<string, string | number | undefined>)}`),

  /**
   * The media inventory. READ-ONLY: there is no approve or hide route.
   *
   * Sending no `state`/`type` gets the server's DEFAULT view — pending video,
   * the only genuine queue. Pass 'all' to widen it.
   */
  getMedia: (params: MediaQuery = {}) =>
    request<MediaResponse>(`/media${qs(params as Record<string, string | number | undefined>)}`),

  suspendBook: (bookId: string, reason: string) =>
    request<SuspendResult>(`/books/${encodeURIComponent(bookId)}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  reactivateBook: (bookId: string) =>
    request<ReactivateResult>(`/books/${encodeURIComponent(bookId)}/reactivate`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  /**
   * `deadlineLocal` is a raw 'YYYY-MM-DDTHH:mm' from <input type="datetime-local">.
   * It is NOT converted here: the server reads the book's couple_timezone and
   * does the conversion in Postgres, so an operator in another timezone cannot
   * shift a couple's deadline by accident.
   */
  setDeadline: (bookId: string, deadlineLocal: string) =>
    request<DeadlineResult>(`/books/${encodeURIComponent(bookId)}/deadline`, {
      method: 'PUT',
      body: JSON.stringify({ deadline_local: deadlineLocal }),
    }),

  resendInvitation: (guestId: string) =>
    request<ResendResult>(`/guests/${encodeURIComponent(guestId)}/resend-invitation`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  getCatalogue: (params: { kind?: CatalogueKind; include_inactive?: boolean } = {}) =>
    request<CatalogueResponse>(
      `/catalogue${qs(params as Record<string, string | number | boolean | undefined>)}`
    ),

  createCatalogueItem: (kind: CatalogueKind, body: Record<string, unknown>) =>
    request<CatalogueWriteResult>(`/catalogue/${kind}`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  updateCatalogueItem: (kind: CatalogueKind, key: string, body: Record<string, unknown>) =>
    request<CatalogueWriteResult>(`/catalogue/${kind}/${encodeURIComponent(key)}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),

  /** Soft by default — the item stops being offered but existing books keep rendering it. */
  deleteCatalogueItem: (kind: CatalogueKind, key: string, hard = false) =>
    request<{ deleted: boolean; deactivated?: boolean; in_use: number; version: number | null }>(
      `/catalogue/${kind}/${encodeURIComponent(key)}${hard ? '?hard=1' : ''}`,
      { method: 'DELETE' }
    ),

  /**
   * REPLACES one tier's grants for one kind — the list sent is the whole list.
   *
   * The server refuses anything that breaks monotonicity (422 NOT_MONOTONIC,
   * carrying `missing` or `extra`), so a sequence of these across tiers has to
   * be ordered: grants go top tier down, revocations bottom tier up. See
   * CatalogueItemDialog.applyTierChanges, which is the only caller.
   */
  setTierCatalogue: (tier: Tier, kind: 'style' | 'edition', itemKeys: string[]) =>
    request<{ tier: string; kind: string; item_keys: string[]; version: number | null }>(
      `/catalogue/tiers/${tier}/${kind}`,
      { method: 'PUT', body: JSON.stringify({ item_keys: itemKeys }) }
    ),

  /**
   * Rewrites `sort_order` for one kind from the order of `keys` — position i
   * becomes (i + 1) * 10. The list must be COMPLETE for that kind: a key left
   * out keeps whatever sort_order it had and can collide with the new run.
   */
  reorderCatalogue: (kind: CatalogueKind, keys: string[]) =>
    request<{ kind: string; ordered: number; version: number | null }>(
      `/catalogue/${kind}/reorder`,
      { method: 'POST', body: JSON.stringify({ keys }) }
    ),

  getConvertedGuests: (params: ConvertedGuestsQuery = {}) =>
    request<ConvertedGuestsResponse>(
      `/conversions/guests${qs(params as Record<string, string | number | undefined>)}`
    ),

  // ── Couples ───────────────────────────────────────────────────────────────

  getCouples: (params: CouplesQuery = {}) =>
    request<CouplesResponse>(`/couples${qs(params as Record<string, string | number | undefined>)}`),

  getCouple: (coupleId: string) =>
    request<CoupleDetail>(`/couples/${encodeURIComponent(coupleId)}`),

  /**
   * Names only. The server also rewrites wedding_books' own copy of the names
   * in the same transaction, because that copy is what the cover prints.
   * Email is deliberately not editable — see the route's header.
   */
  updateCouple: (coupleId: string, body: { partner1_name: string; partner2_name: string }) =>
    request<{ couple: CoupleRow; books_updated: number }>(
      `/couples/${encodeURIComponent(coupleId)}`,
      { method: 'PUT', body: JSON.stringify(body) }
    ),

  /**
   * Sends the couple their own reset email. Staff never see or set a password.
   * Throws `RESET_RATE_LIMITED` (429) when the wedding service's 3-per-hour
   * limiter refuses — that is a "wait", not a failure to report as broken.
   */
  resetCouplePassword: (coupleId: string) =>
    request<CoupleAuthActionResult>(
      `/couples/${encodeURIComponent(coupleId)}/password-reset`,
      { method: 'POST', body: JSON.stringify({}) }
    ),

  resendCoupleVerification: (coupleId: string) =>
    request<CoupleAuthActionResult>(
      `/couples/${encodeURIComponent(coupleId)}/resend-verification`,
      { method: 'POST', body: JSON.stringify({}) }
    ),

  unlockCouple: (coupleId: string) =>
    request<{ couple: CoupleRow }>(`/couples/${encodeURIComponent(coupleId)}/unlock`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  /**
   * Comp tier change. No money moves and no purchase row is written, so this
   * will not appear in the Revenue page's upgrade rate.
   *
   * Two refusals the dialog must handle rather than treat as errors:
   *   · `TIER_DOWNGRADE`      — retry with `override_downgrade: true`
   *   · `OVER_CONTRIBUTOR_CAP` — the book has more contributors than the
   *     destination tier allows; retry with `acknowledge_over_cap: true`.
   *     Existing contributors keep writing; only NEW ones are blocked.
   */
  changeBookTier: (
    bookId: string,
    body: {
      tier: Tier;
      reason: string;
      override_downgrade?: boolean;
      acknowledge_over_cap?: boolean;
      activate_book?: boolean;
    }
  ) =>
    request<TierChangeResult>(`/books/${encodeURIComponent(bookId)}/tier`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // ── Members ───────────────────────────────────────────────────────────────

  getMembers: (params: RangeQuery = {}) =>
    request<MembersResponse>(`/members${qs(params as Record<string, string | undefined>)}`),

  getMembersList: (params: MembersListQuery = {}) =>
    request<MembersListResponse>(
      `/members/list${qs(params as Record<string, string | number | undefined>)}`
    ),

  // ── Email template registry ───────────────────────────────────────────────
  //
  // Every mutation can answer with a machine `code` the page branches on:
  // REGISTRY_NOT_MIGRATED (409, the table is not in this database),
  // EDIT_PUBLISHED (409, edit a published row → start a new version instead),
  // DRAFT_EXISTS (409, `details.draft_id` / `details.draft_version` point at
  // the open one), NOT_A_DRAFT, ALREADY_LIVE, NOT_LIVE, PUBLISH_CONFLICT,
  // RETIRE_CONFLICT, KEY_TAKEN, TEMPLATE_NOT_FOUND (404), INVALID_* (400),
  // NO_OPERATOR_IDENTITY (403).

  getTemplates: () => request<TemplatesResponse>('/templates'),

  /** Creates a DRAFT at version 1. The key is derived server-side from the name. */
  createTemplate: (body: TemplateWriteBody) =>
    request<TemplateCreateResult>('/templates', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Drafts only — a published version is history and answers EDIT_PUBLISHED. */
  updateTemplate: (id: string, body: TemplateWriteBody) =>
    request<{ template: TemplateVersion }>(`/templates/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /** Draft → live in one transaction. Sends nothing; makes it selectable. */
  publishTemplate: (id: string) =>
    request<TemplatePublishResult>(`/templates/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  /** Copies a live version into the next draft, leaving the live one untouched. */
  newTemplateVersion: (id: string) =>
    request<TemplateNewVersionResult>(`/templates/${encodeURIComponent(id)}/new-version`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  retireTemplate: (id: string) =>
    request<TemplateRetireResult>(`/templates/${encodeURIComponent(id)}/retire`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  getTemplateHistory: (registryKey: string) =>
    request<TemplateHistoryResponse>(
      `/templates/${encodeURIComponent(registryKey)}/history`
    ),
};

export default weddingApi;
